import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { MdChevronLeft, MdChevronRight, MdOutlineSettings } from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import { GET_OWNER_BRIEF, GET_OWNER_BRIEF_SETTINGS, SEND_OWNER_BRIEF_NOW, UPDATE_OWNER_BRIEF_SETTINGS } from "./graphql";

// The owner's morning brief: how a day went, what needs a decision, and
// what the next day looks like. The same brief is emailed at 9 AM.

const rupees = (v) => `₹${Math.round(v || 0).toLocaleString("en-IN")}`;
const vsLastWeek = (now, before) => {
  if (!before) return null;
  const d = Math.round(((now - before) / before) * 100);
  return { text: `${d >= 0 ? "↑" : "↓"} ${Math.abs(d)}% vs same day last week`, good: d >= 0 };
};

const Card = ({ title, right, children }) => (
  <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
    <header className="flex items-center justify-between gap-2 px-4 pt-4">
      <h2 className="text-xs uppercase tracking-wide font-semibold text-gray-500">{title}</h2>
      {right}
    </header>
    <div className="p-4 pt-2">{children}</div>
  </section>
);

const Row = ({ label, value, sub, tone }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-sm text-gray-600">{label}</span>
    <span className="text-right">
      <span className={`text-sm font-semibold tabular-nums ${tone || "text-gray-900"}`}>{value}</span>
      {sub && <span className="block text-xs text-gray-400">{sub}</span>}
    </span>
  </div>
);

const Settings = ({ onClose }) => {
  const { data, loading } = useQuery(GET_OWNER_BRIEF_SETTINGS, { fetchPolicy: "network-only" });
  const [save, { loading: saving }] = useMutation(UPDATE_OWNER_BRIEF_SETTINGS);
  const res = data?.getOwnerBriefSettings;
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (res?.settings && !form) setForm({ enabled: res.settings.enabled, emails: res.settings.emails.join(", "), pushAdminIds: res.settings.pushAdminIds });
  }, [res, form]);

  if (loading || !form) return <div className="p-4 text-sm text-gray-400">Loading…</div>;
  if (res?.status === "ERROR") return <div className="p-4 text-sm text-red-600">{res.message}</div>;

  const togglePush = (id) =>
    setForm((f) => ({ ...f, pushAdminIds: f.pushAdminIds.includes(id) ? f.pushAdminIds.filter((x) => x !== id) : [...f.pushAdminIds, id] }));

  const onSave = async () => {
    setMsg("");
    const emails = form.emails.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean);
    const out = await save({ variables: { data: { enabled: form.enabled, emails, pushAdminIds: form.pushAdminIds } } });
    const r = out.data?.updateOwnerBriefSettings;
    if (r?.status === "SUCCESS") {
      setForm({ enabled: r.settings.enabled, emails: r.settings.emails.join(", "), pushAdminIds: r.settings.pushAdminIds });
      setMsg(emails.length !== r.settings.emails.length ? "Saved. Some email addresses weren't valid and were left out." : "Saved.");
    } else setMsg(r?.message || "Could not save.");
  };

  return (
    <div className="space-y-4 text-sm">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
        <span className="font-medium text-gray-900">Send the brief every morning at 9 AM</span>
      </label>
      <div>
        <p className="font-medium text-gray-900">Email to</p>
        <p className="text-xs text-gray-500 mb-1">Comma-separated. Sent from the operations mailbox.</p>
        <input value={form.emails} onChange={(e) => setForm((f) => ({ ...f, emails: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2" placeholder="you@example.com" />
      </div>
      <div>
        <p className="font-medium text-gray-900">Phone notification to</p>
        <p className="text-xs text-gray-500 mb-1">Only the people you tick. The office doesn't get the brief unless you add them.</p>
        <div className="grid sm:grid-cols-2 gap-1">
          {res.admins.map((a) => (
            <label key={a.id} className={`flex items-center gap-2 ${a.hasDevice ? "" : "opacity-50"}`}>
              <input type="checkbox" disabled={!a.hasDevice} checked={form.pushAdminIds.includes(a.id)} onChange={() => togglePush(a.id)} />
              {a.fullName || "Unnamed"}
              {!a.hasDevice && <span className="text-xs text-gray-400">(no app)</span>}
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={onSave} disabled={saving} className="px-4 py-2 rounded-lg bg-gray-900 text-white font-medium disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-900">Close</button>
        {msg && <span className="text-gray-600">{msg}</span>}
      </div>
    </div>
  );
};

const DailyBrief = () => {
  const [params, setParams] = useSearchParams();
  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  const date = params.get("date") || yesterday;
  const [showSettings, setShowSettings] = useState(false);
  const [sendMsg, setSendMsg] = useState(null);

  const { data, loading, error } = useQuery(GET_OWNER_BRIEF, { variables: { date }, fetchPolicy: "cache-and-network" });
  const [sendNow, { loading: sending }] = useMutation(SEND_OWNER_BRIEF_NOW);
  const res = data?.getOwnerBrief;
  const b = res?.status === "SUCCESS" ? res.data : null;
  const errorMessage = error?.message || (res?.status === "ERROR" && res.message);

  const go = (d) => setParams(d === yesterday ? {} : { date: d });
  const shift = (n) => go(dayjs(date).add(n, "day").format("YYYY-MM-DD"));

  const onSend = async () => {
    setSendMsg(null);
    const out = await sendNow({ variables: { date } });
    const r = out.data?.sendOwnerBriefNow;
    setSendMsg({ ok: r?.status === "SUCCESS", text: r?.message || "Nothing happened." });
  };

  const trend = b ? vsLastWeek(b.bookings.revenue, b.bookings.lastWeekRevenue) : null;
  const red = b ? b.alerts.filter((a) => a.level === "red") : [];
  const amber = b ? b.alerts.filter((a) => a.level !== "red") : [];

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-5xl space-y-5 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-white">Daily brief</h1>
              <p className="text-sm text-gray-400 mt-0.5">One day at a glance, and what needs your decision. Emailed every morning once switched on.</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-white rounded-lg">
                <button type="button" onClick={() => shift(-1)} className="p-2 text-gray-600 hover:text-gray-900" aria-label="Previous day"><MdChevronLeft /></button>
                <input type="date" value={date} max={dayjs().format("YYYY-MM-DD")} onChange={(e) => e.target.value && go(e.target.value)} className="text-sm px-1 py-1.5 outline-none" />
                <button type="button" onClick={() => shift(1)} disabled={date >= dayjs().format("YYYY-MM-DD")} className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-30" aria-label="Next day"><MdChevronRight /></button>
              </div>
              <button type="button" onClick={onSend} disabled={sending} className="px-3 py-2 rounded-lg bg-[#FBB033] text-gray-900 text-sm font-semibold disabled:opacity-50">
                {sending ? "Sending…" : "Send this now"}
              </button>
              <button type="button" onClick={() => setShowSettings((s) => !s)} className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20" aria-label="Brief settings"><MdOutlineSettings className="text-xl" /></button>
            </div>
          </div>

          {sendMsg && <div className={`rounded-xl px-4 py-3 text-sm border ${sendMsg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}>{sendMsg.text}</div>}
          {showSettings && (
            <Card title="Who gets the brief">
              <Settings onClose={() => setShowSettings(false)} />
            </Card>
          )}
          {errorMessage && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not build the brief: {errorMessage}</div>}
          {loading && !b && <div className="py-16 text-center text-gray-400">Building the brief…</div>}

          {b && (
            <>
              <section className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">{dayjs(b.date).format("dddd, D MMMM")}</p>
                  <p className="text-4xl font-semibold text-gray-900 tabular-nums mt-1">{rupees(b.bookings.revenue)}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {b.bookings.completed} bookings completed
                    {trend && <span className={`ml-2 ${trend.good ? "text-emerald-600" : "text-red-600"}`}>{trend.text}</span>}
                  </p>
                </div>
                <div className="flex gap-6 text-sm">
                  <div>
                    <p className="text-gray-500">Bookings made</p>
                    <p className="text-xl font-semibold tabular-nums">{b.bookings.bookingsMade}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">New customers</p>
                    <p className="text-xl font-semibold tabular-nums">{b.bookings.newCustomers}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Rating</p>
                    <p className="text-xl font-semibold tabular-nums">{b.bookings.avgRating === null ? "—" : `${b.bookings.avgRating}★`}</p>
                  </div>
                </div>
              </section>

              <Card title={`Needs your attention (${b.alerts.length})`}>
                {!b.alerts.length && <p className="text-sm text-emerald-700">Nothing needs your attention.</p>}
                <ul className="space-y-2">
                  {[...red, ...amber].map((a, i) => (
                    <li key={i} className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${a.level === "red" ? "bg-red-50 border-red-100 text-red-800" : "bg-amber-50 border-amber-100 text-amber-900"}`}>
                      <span>{a.text}</span>
                      {a.link && <Link to={a.link} className="shrink-0 font-medium underline">Open</Link>}
                    </li>
                  ))}
                </ul>
              </Card>

              <div className="grid md:grid-cols-2 gap-5">
                <Card title="The day">
                  <Row label="Cancelled" value={b.bookings.cancelled} sub={`customer ${b.bookings.cancelledByCustomer} · office ${b.bookings.cancelledByOffice} · professional ${b.bookings.cancelledByProfessional}`} tone={b.bookings.cancelledByOffice ? "text-amber-600" : undefined} />
                  <Row label="Declined" value={b.bookings.declined} />
                  <Row label="On hold" value={b.bookings.onHold} />
                  <Row label="Never closed" value={b.bookings.notClosed} tone={b.bookings.notClosed ? "text-red-600" : undefined} />
                  <Row label="Low ratings (1–2★)" value={b.bookings.lowRatings} sub={b.bookings.lowRatingsNotCalled ? `${b.bookings.lowRatingsNotCalled} not called back` : "all called back"} tone={b.bookings.lowRatingsNotCalled ? "text-red-600" : undefined} />
                </Card>
                <Card title={`Next day · ${dayjs(b.todayDate).format("D MMM")}`}>
                  <Row label="Booked" value={b.today.scheduled} sub={rupees(b.today.value)} />
                  <Row label="No professional assigned" value={b.today.unassigned} tone={b.today.unassigned ? "text-red-600" : undefined} />
                  <Row label="On leave" value={b.today.onLeave.length} sub={b.today.onLeave.join(", ") || null} />
                  <Row label="Leave requests waiting" value={b.today.pendingLeaveRequests} tone={b.today.pendingLeaveRequests ? "text-amber-600" : undefined} />
                </Card>
                <Card title="Money" right={<Link to="/admin/money-watch" className="text-xs text-gray-500 underline">Money watch</Link>}>
                  <Row label="Cash with professionals" value={rupees(b.money.cashHeld)} sub={`${rupees(b.money.cashOverdue)} older than ${b.money.cashDays} days`} tone={b.money.cashOverdue ? "text-red-600" : undefined} />
                  <Row label="Paid online, not Razorpay" value={rupees(b.money.onlineOutsideRazorpay)} sub={`${b.money.onlineOutsideRazorpayBookings} bookings this day`} />
                  <Row label="Not verified" value={b.money.unverifiedBookings} tone={b.money.unverifiedBookings ? "text-amber-600" : undefined} />
                  <Row label="Razorpay unmatched" value={rupees(b.money.unlinkedRazorpay)} sub={`${b.money.unlinkedRazorpayCount} payments`} />
                  <Row label="Wallet credited by office" value={rupees(b.money.walletCredited)} />
                </Card>
                <Card title="In the field" right={<Link to="/admin/employee-activity" className="text-xs text-gray-500 underline">Employee activity</Link>}>
                  {b.field ? (
                    <>
                      <Row label="Start vs slot (median)" value={b.field.medianStartDelay === null ? "—" : `${b.field.medianStartDelay} min`} sub={b.field.onTimePct === null ? null : `${b.field.onTimePct}% on time`} />
                      <Row label="30+ min late" value={b.field.startsVeryLate} sub={b.field.veryLate.map((v) => `${v.name} (${v.count})`).join(", ") || null} tone={b.field.startsVeryLate ? "text-red-600" : undefined} />
                      <Row label="Late, customer not told" value={b.field.silentLate} tone={b.field.silentLate ? "text-amber-600" : undefined} />
                      <Row label="Holds" value={b.field.holds} sub={`${b.field.holdsReleased} released`} />
                      <Row label="Service timer used" value={b.field.timerPct === null ? "—" : `${b.field.timerPct}%`} sub={`${b.field.timerLines} timed services`} />
                      <Row label="Not at the customer (GPS)" value={b.field.locFar ?? 0} sub={`${b.field.locMocked ?? 0} fake GPS · ${b.field.locNoFix ?? 0} no location`} tone={b.field.locFar || b.field.locMocked ? "text-red-600" : undefined} />
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">Not available.</p>
                  )}
                </Card>
              </div>

              {b.demand && (
                <Card title="Demand" right={<Link to="/admin/lost-demand" className="text-xs text-gray-500 underline">Lost demand</Link>}>
                  <Row label="Looked for a slot" value={b.demand.searches} />
                  <Row label="Found none and left" value={b.demand.lost} sub={b.demand.lostValue ? `about ${rupees(b.demand.lostValue)} in carts` : null} tone={b.demand.lost ? "text-red-600" : undefined} />
                  <Row label="Found none, booked anyway" value={b.demand.recovered} />
                  {b.demand.fullDates.length > 0 && <Row label="Full dates asked for" value={b.demand.fullDates.map((x) => dayjs(x).format("D MMM")).join(", ")} />}
                </Card>
              )}

              <Card title="Office" right={<Link to="/admin/office-activity" className="text-xs text-gray-500 underline">Every change</Link>}>
                {!b.office.length && <p className="text-sm text-gray-400">No admin changes recorded this day.</p>}
                {b.office.map((p) => (
                  <Row
                    key={p.name || "unknown"}
                    label={p.name || "Unknown"}
                    value={`${p.actions} changes`}
                    sub={[p.cancellations && `${p.cancellations} cancelled`, p.walletCredited && `${rupees(p.walletCredited)} wallet`, p.deletions && `${p.deletions} deleted`, p.afterHours && `${p.afterHours} after hours`].filter(Boolean).join(" · ") || null}
                  />
                ))}
              </Card>
            </>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default DailyBrief;
